import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, Modal, TextareaField } from "@fawkes/ui";
import { Sparkles } from "lucide-react";

/** De velden die de AI-hulp alvast kan invullen in het aanmaakformulier van een tariefscherm. */
export interface TariefSeed {
  naam?: string;
  diersoortId?: string;
  bedrag?: string;
  bedragHoog?: string;
  effectdatum?: string;
}

interface AiVoorstel {
  actie: "NIEUW" | "WIJZIG" | "VERWIJDER";
  /** Tekst, want het crematorium gebruikt UUIDs waar de andere schermen gehele getallen hebben. */
  tariefId: string | null;
  naam: string | null;
  diersoortNaam: string | null;
  bedrag: number | null;
  bedragHoog: number | null;
  ingangsdatum: string | null;
  volledig: boolean;
  uitleg: string;
}

/**
 * Of de AI-hulp op dit scherm beschikbaar is.
 *
 * <p>Er is geen instelling die de browser kan uitlezen: staat de vlag
 * {@code fawkes.producten.ai-features.enabled} uit, dan bestaat de route niet en geeft élke aanroep —
 * ook deze GET — een 404. Staat hij aan, dan bestaat de route maar accepteert hij alleen POST, dus
 * geeft de GET een 405. Een 404 betekent dus "uit", al het andere "aan".
 */
export function useAiBeschikbaar(pad: string): boolean {
  const vast = aiBekend.get(pad);
  const { error, isFetched, isLoading } = useCustom({
    url: pad,
    method: "get",
    // Mislukken hoort hier bij het antwoord; geen foutmelding in beeld.
    errorNotification: false,
    queryOptions: {
      // Eenmaal bekend vragen we het niet nog eens: geen verzoek, dus ook niets dat kan terugspringen.
      enabled: vast === undefined,
      retry: false,
      // Of de vlag aanstaat verandert niet tijdens het werken, dus dit hoeft maar een keer.
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  });

  /**
   * Het antwoord blijft staan zodra het bekend is, en wel buiten het scherm (Maarten, 2026-10-12: "wil geen
   * disco").
   *
   * <p>De knop knipperde. Eerst omdat hij aanstond zolang het antwoord er niet was en uitging zodra de 404 kwam;
   * daarna omdat elke keer dat deze navraag opnieuw begon (na elke opslag ververst alles) het antwoord even
   * "weet ik niet" was. Een grendel per scherm hielp niet genoeg: wordt het scherm opnieuw opgebouwd, dan is
   * die weg. Daarom staat het antwoord hier per pad voor de hele sessie vast. Of de vlag aanstaat hangt aan een
   * serverinstelling en verandert niet terwijl iemand werkt.
   */
  const nu = isFetched && !isLoading
    ? (error as { statusCode?: number } | undefined)?.statusCode !== 404
    : null;
  if (vast === undefined && nu !== null) {
    aiBekend.set(pad, nu);
  }

  // Tot het bekend is: geen knop. Liever een knop die even later verschijnt dan een die knippert.
  return vast ?? nu ?? false;
}

/** Per pad of de AI-hulp bestaat; gevuld bij de eerste navraag en daarna niet meer aangeraakt. */
const aiBekend = new Map<string, boolean>();

/** Alleen voor tests: vergeet wat er over de AI-hulp bekend is. */
export function wisAiBekend(): void {
  aiBekend.clear();
}

/** De knop die de hulp opent. Los van de dialoog, zodat elk scherm hem in zijn eigen balk kan zetten. */
export function AiHulpKnop({ onClick }: { onClick: () => void }) {
  const t = useTranslate();
  return (
    <Button variant="secondary" onClick={onClick}>
      <Sparkles size={16} /> {t("tariefAi.action")}
    </Button>
  );
}

/**
 * De AI-hulp bij een tariefscherm: u beschrijft wat u wilt, en dit leest daar een opdracht uit — een
 * tarief aanmaken, een prijs wijzigen of een tarief verwijderen.
 *
 * <p>Wat hier gebeurt is nadrukkelijk niet uitvoeren maar vóórinvullen: het voorstel wordt getoond,
 * en pas als u op de knop drukt gaat het naar hetzelfde formulier dat u anders zelf had ingevuld.
 * Verwijderen loopt langs dezelfde bevestiging als een verwijdering die u zelf aanklikt. Zo kan een
 * verkeerd gelezen zin nooit stilzwijgend iets veranderen.
 *
 * <p>Niet elk scherm kan alle drie: de dierenambulance kent alleen prijswijzigingen. Een handeling
 * waarvoor het scherm geen formulier heeft, laat de knop uitgeschakeld met de reden erbij — beter dan
 * een knop die niets doet.
 */
export function AiTariefHulp({ pad, voorbeeld, diersoorten = [], vandaag, onClose, onNieuw, onBewerk, onVerwijder }: {
  /** Het eindpunt van dit scherm, bijvoorbeeld "pension-tarief/ai-suggestie". */
  pad: string;
  /** Een voorbeeldzin die bij dit scherm past, als tijdelijke tekst in het invoerveld. */
  voorbeeld: string;
  /** De soortenlijst van het scherm, om een herkende diersoortnaam naar een id te vertalen. */
  diersoorten?: { id: number; label: string }[];
  /** De datum die geldt als de beschrijving er geen noemt. */
  vandaag: string;
  onClose: () => void;
  onNieuw?: (seed: TariefSeed) => void;
  onBewerk?: (tariefId: string) => void;
  onVerwijder?: (tariefId: string) => void;
}) {
  const t = useTranslate();
  const [tekst, setTekst] = useState("");
  const [voorstel, setVoorstel] = useState<AiVoorstel | null>(null);
  const [fout, setFout] = useState(false);
  const { mutate, isLoading } = useCustomMutation();

  const vraag = () => {
    if (tekst.trim() === "") return;
    setFout(false);
    setVoorstel(null);
    mutate(
      { url: pad, method: "post", values: { tekst }, successNotification: false, errorNotification: false },
      {
        onSuccess: (res) => setVoorstel(res.data as unknown as AiVoorstel),
        onError: () => setFout(true),
      },
    );
  };

  const behandelaar = voorstel
    ? { NIEUW: onNieuw, WIJZIG: onBewerk, VERWIJDER: onVerwijder }[voorstel.actie]
    : undefined;

  const toepassen = () => {
    if (!voorstel?.volledig) return;
    if (voorstel.actie === "NIEUW") {
      const soort = diersoorten.find((d) => d.label === voorstel.diersoortNaam);
      onNieuw?.({
        naam: voorstel.naam ?? "",
        diersoortId: soort ? String(soort.id) : "",
        bedrag: voorstel.bedrag != null ? String(voorstel.bedrag) : "",
        bedragHoog: voorstel.bedragHoog != null ? String(voorstel.bedragHoog) : "",
        effectdatum: voorstel.ingangsdatum ?? vandaag,
      });
    } else if (voorstel.tariefId != null) {
      (voorstel.actie === "WIJZIG" ? onBewerk : onVerwijder)?.(voorstel.tariefId);
    }
  };

  const nietOpDitScherm = !!voorstel?.volledig && !behandelaar;
  const nietGevonden = !!voorstel?.volledig && voorstel.actie !== "NIEUW" && voorstel.tariefId == null;
  const kanToepassen = !!voorstel?.volledig && !!behandelaar && !nietGevonden;

  return (
    <Modal
      open
      title={t("tariefAi.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.close")}</Button>
          {voorstel && (
            <Button onClick={toepassen} disabled={!kanToepassen}>
              {t("tariefAi.apply." + voorstel.actie)}
            </Button>
          )}
        </>
      }
    >
      <div className="asiel-tarief__form">
        <p className="fawkes-muted">{t("tariefAi.intro")}</p>
        <TextareaField
          label={t("tariefAi.veld")}
          placeholder={voorbeeld}
          value={tekst}
          rows={3}
          onChange={(e) => setTekst(e.target.value)}
        />
        <div>
          <Button variant="secondary" onClick={vraag} disabled={isLoading || tekst.trim() === ""}>
            {t("tariefAi.submit")}
          </Button>
        </div>
        {fout && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">{t("tariefAi.error")}</div>
        )}
        {voorstel && (
          <div className="asiel-tarief__notice" data-testid="ai-voorstel">
            {voorstel.uitleg}
            {nietGevonden && <div>{t("tariefAi.notFound")}</div>}
            {nietOpDitScherm && <div>{t("tariefAi.notHere")}</div>}
          </div>
        )}
      </div>
    </Modal>
  );
}
